import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { BaseDatatableStore } from '../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseCrudResponse,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../utilities/http-utilities.service';
import { IEditForms, IForms, TAddFormSetting, TFormBulkEditRequest } from './form.interface';

export interface IFormsComponentState {
  bulkCrudLoading: ELoadStatus;
  bulkOperationFailedData: IBaseCrudResponse[];
  data: IForms[];
  dataLoading: ELoadStatus;
  dataTotal: number;
  singleCrudLoading: ELoadStatus;
}

@Injectable()
export class FormStore extends BaseDatatableStore<IForms, IFormsComponentState> {
  readonly createOne = this.effect((trigger$: Observable<TAddFormSetting>) =>
    trigger$.pipe(
      switchMap((item: TAddFormSetting) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.createItem(item).pipe(
          tapResponse(
            (): void => {
              this.patchState({ singleCrudLoading: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console,sonarjs/no-duplicate-string
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );
  readonly updateOne = this.effect((trigger$: Observable<IEditForms>) =>
    trigger$.pipe(
      switchMap((item: IEditForms) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.updateItem(item).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteOne = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id: number) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.deleteItem(id).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteBulk = this.effect((trigger$: Observable<number[]>) =>
    trigger$.pipe(
      switchMap((itemIds: number[]) => {
        this.patchState({ bulkCrudLoading: ELoadStatus.loading });

        return this.deleteItems(itemIds).pipe(
          tapResponse(
            (response): void => {
              this.patchState({ bulkCrudLoading: ELoadStatus.success, bulkOperationFailedData: response.data });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadItems = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getItems(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IForms>) =>
              this.patchState({ data: response.data, dataLoading: ELoadStatus.success, dataTotal: response.total }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly updateMany = this.effect((trigger$: Observable<TFormBulkEditRequest[]>) =>
    trigger$.pipe(
      switchMap((dto: TFormBulkEditRequest[]) => {
        this.patchState({ bulkCrudLoading: ELoadStatus.loading });

        return this.bulkEditForms(dto).pipe(
          tapResponse(
            (response): void => {
              this.patchState({ bulkCrudLoading: ELoadStatus.success, bulkOperationFailedData: response.data });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      bulkCrudLoading: ELoadStatus.initial,
      bulkOperationFailedData: [],
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
      singleCrudLoading: ELoadStatus.initial,
    });
  }

  private createItem(body: TAddFormSetting): Observable<IBaseResponse<IForms>> {
    return this.http.post<IBaseResponse<IForms>>('forms', body);
  }

  private updateItem(editForms: IEditForms): Observable<IBaseResponse<IForms>> {
    return this.http.patch<IBaseResponse<IForms>>(`forms/${editForms.id}`, editForms.dto);
  }

  private deleteItem(id: number): Observable<IBaseResponse<IForms>> {
    return this.http.delete<IBaseResponse<IForms>>(`forms/${id}`);
  }

  private getItems(params: HttpParams): Observable<IGetManyResponse<IForms>> {
    return this.http.get<IGetManyResponse<IForms>>('forms', { params });
  }

  private deleteItems(ids: number[]): Observable<IBaseResponse<IBaseCrudResponse[]>> {
    return this.http.delete<IBaseResponse<IBaseCrudResponse[]>>('forms/bulk/delete', {
      body: { payload: ids },
    });
  }

  private bulkEditForms(bulkEditFieldSet: TFormBulkEditRequest[]): Observable<IBaseResponse<IBaseCrudResponse[]>> {
    return this.http.patch<IBaseResponse<IBaseCrudResponse[]>>('forms/bulk/update', { payload: bulkEditFieldSet });
  }
}
